import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePlans } from '../../hooks/usePlan';
import { useFeatureLimitsForPlan } from '../../hooks/useFeatureLimits';
import { useSubscription } from '../../hooks/useSubscription';
import { useAuthenticatedUserInfo } from '../../services/Contexts/AuthenticatedUserInfoContext';
import { getDashboardPathForRole } from '../../utils/dashboardNavigation';
import PlanUpgradePicker from '../../components/Auth/SubscriptionPlan/PlanUpgradePicker';
import { isLifetimePlanRecord } from '../../components/Auth/SubscriptionPlan/subscriptionPlanMatrixContext';
import SubscriptionModal from '../../components/Auth/SubscriptionPlan/EmbeddedCheckout/SubscriptionModal';
import LifetimeCheckoutModal from '../../components/Auth/SubscriptionPlan/EmbeddedCheckout/LifetimeCheckoutModal';
import AppSumoLicenseActivateCard from '../../components/Auth/SubscriptionPlan/AppSumoLicenseActivateCard';
import {
	captureAppSumoParamsFromSearch,
	hasAppSumoFlow,
} from '../../utils/appsumoSession';
import {
	Box,
	Heading,
	Text,
	Spinner,
	HStack,
	VStack,
	Center,
	useDisclosure,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalBody,
	Button,
	Icon,
	Flex,
	Badge,
	Skeleton,
} from '@chakra-ui/react';
import { FaCrown, FaCheckCircle, FaShieldAlt, FaRocket } from 'react-icons/fa';
import defaultSizes from '../../utils/defaultSizes';

const SubscribeToPlanFirstTimePage = ({
	plans: monthlyPlans,
	plansYear: yearlyPlans,
}) => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const queryClient = useQueryClient();
	const { userRole, companyId } = useAuthenticatedUserInfo();
	const { refetch: refetchSubscription, data: subscription } = useSubscription(companyId);
	const showMoneyBackGuarantee = subscription?.hasNeverHadPlan !== false;
	const [isNavigating, setIsNavigating] = useState(false);
	const { data: allPlans, isLoading: loadingPlans } = usePlans();
	const [isYearly, setIsYearly] = useState(false);
	const [selectedPlan, setSelectedPlan] = useState(null);
	const [selectedLifetimePlan, setSelectedLifetimePlan] = useState(null);
	const [successData, setSuccessData] = useState(null);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const {
		isOpen: isLifetimeOpen,
		onOpen: onLifetimeOpen,
		onClose: onLifetimeClose,
	} = useDisclosure();
	const {
		isOpen: isSuccessOpen,
		onOpen: onSuccessOpen,
		onClose: onSuccessClose,
	} = useDisclosure();

	useEffect(() => {
		captureAppSumoParamsFromSearch(searchParams.toString());
	}, [searchParams]);

	const isAppSumoFlow = useMemo(
		() => searchParams.get('appsumo') === '1' || hasAppSumoFlow(),
		[searchParams],
	);

	const subscribedPlanId =
		selectedPlan?._id ||
		selectedPlan?.id ||
		selectedLifetimePlan?._id ||
		selectedLifetimePlan?.id ||
		successData?.plan?.id ||
		successData?.subscription?.planId;
	const { data: featureLimitsResponse, isLoading: loadingBrandingCheck } =
		useFeatureLimitsForPlan(isSuccessOpen ? subscribedPlanId : null);
	const hasBrandingAccess =
		featureLimitsResponse?.data?.data?.branded_workspace != null;

	const allPlansForPicker = useMemo(() => {
		const source = monthlyPlans || allPlans || [];
		const subscription = source.filter((plan) => !isLifetimePlanRecord(plan));
		const lifetime = (allPlans || []).filter((plan) => isLifetimePlanRecord(plan));
		return [...subscription, ...lifetime];
	}, [monthlyPlans, allPlans]);

	const activePlanName =
		successData?.subscription?.planName ||
		successData?.plan?.planName ||
		selectedLifetimePlan?.planName ||
		selectedPlan?.planName;

	const handlePlanSignUp = (plan) => {
		setSelectedPlan({
			...plan,
			price: 0,
			stripePriceId: isYearly
				? plan.stripePriceIdYearly
				: plan.stripePriceIdMonthly,
			isUpgrade: false,
			currentSubscription: null,
		});

		setTimeout(() => onOpen(), 100);
	};

	const refreshSubscriptionState = async () => {
		await queryClient.invalidateQueries({ queryKey: ['subscription', companyId] });
		await queryClient.invalidateQueries({ queryKey: ['companies', companyId] });
		await queryClient.refetchQueries({ queryKey: ['companies', companyId] });
		const result = await refetchSubscription();
		return result.data?.hasActiveSubscription === true;
	};

	const handleSubscriptionSuccess = (subscriptionData) => {
		onClose();
		setSuccessData(subscriptionData);
		onSuccessOpen();
	};

	const handleLifetimePurchaseSuccess = async (data) => {
		await refreshSubscriptionState();
		setSuccessData(data);
		onSuccessOpen();
	};

	const handleAppSumoActivateSuccess = async (data) => {
		await refreshSubscriptionState();
		setSuccessData(data);
		onSuccessOpen();
	};

	const handleLifetimePlanSelect = (plan) => {
		setSelectedLifetimePlan(plan);
		onLifetimeOpen();
	};

	const handleGetToWork = async () => {
		setIsNavigating(true);
		try {
			await refreshSubscriptionState();
			onSuccessClose();

			if (hasBrandingAccess) {
				navigate('/settings?section=branding', { replace: true });
				return;
			}
			navigate(getDashboardPathForRole(userRole), { replace: true });
		} finally {
			setIsNavigating(false);
		}
	};

	if (loadingPlans) {
		return (
			<Box  bg='gray.50' px={4} py={10}>
				<Center>
					<VStack spacing={6} w='full' maxW='1100px'>
						<Skeleton height='200px' borderRadius='2xl' w='full' />
						<Skeleton height='400px' borderRadius='2xl' w='full' />
					</VStack>
				</Center>
			</Box>
		);
	}

	return (
		<Box  w={'full'}>
			<Box >
				{/* Hero */}
				<Box
					bgGradient='linear(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
					borderRadius='2xl'
					p={{ base: 6, md: 10 }}
					mb={8}
					color='white'
					position='relative'
					overflow='hidden'>
					<Box
						position='absolute'
						top='-60px'
						right='-60px'
						w='220px'
						h='220px'
						borderRadius='full'
						bg='whiteAlpha.50'
						pointerEvents='none'
					/>
					<Box
						position='absolute'
						bottom='-40px'
						left='20%'
						w='140px'
						h='140px'
						borderRadius='full'
						bg='whiteAlpha.50'
						pointerEvents='none'
					/>

					<VStack spacing={6} align='stretch' position='relative' zIndex={1}>
						<Box textAlign='center'>
							<HStack justify='center' mb={3} spacing={2}>
								<Icon as={FaCrown} color='yellow.300' boxSize={4} />
								<Text
									fontSize='xs'
									color='whiteAlpha.700'
									fontWeight='600'
									textTransform='uppercase'
									letterSpacing='0.14em'
									m={0}>
									{isAppSumoFlow ? 'AppSumo activation' : 'Choose your plan'}
								</Text>
							</HStack>
							<Heading
								as='h2'
								color={'white'}
								fontSize={{ base: '2xl', md: '3xl', lg: '5xl' }}
								fontWeight='800'
								lineHeight='1.15'
								mb={3}>
								{isAppSumoFlow
									? 'Activate your AppSumo lifetime access'
									: "We've got a plan that's perfect for you"}
							</Heading>
							<Text
								fontSize={{ base: 'md', md: 'lg' }}
								color='whiteAlpha.800'
								maxW='560px'
								mx='auto'>
								{isAppSumoFlow
									? 'You already purchased on AppSumo. Activate your deal below, or choose a different plan if you prefer.'
									: 'Choose a subscription or buy lifetime access in-app — all without leaving OpsPilot.'}
							</Text>
						</Box>

						<Flex
							justify='center'
							flexWrap='wrap'
							gap={3}
							pt={1}>
							{showMoneyBackGuarantee ? (
								<HStack
									spacing={2}
									px={4}
									py={2}
									borderRadius='full'
									bg='whiteAlpha.150'
									border='1px solid'
									borderColor='whiteAlpha.200'>
									<Icon as={FaCheckCircle} color='green.300' boxSize={4} />
									<Text fontSize='sm' m={0} fontWeight='500'>
										30-day money-back guarantee
									</Text>
								</HStack>
							) : null}
							<HStack
								spacing={2}
								px={4}
								py={2}
								borderRadius='full'
								bg='whiteAlpha.150'
								border='1px solid'
								borderColor='whiteAlpha.200'>
								<Icon as={FaShieldAlt} color='teal.200' boxSize={3} />
								<Text fontSize='sm' m={0} fontWeight='500'>
									Secure Stripe checkout
								</Text>
							</HStack>
						</Flex>
					</VStack>
				</Box>

				{isAppSumoFlow ? (
					<Box mb={6}>
						<AppSumoLicenseActivateCard onActivateSuccess={handleAppSumoActivateSuccess} />
						<Text
							fontSize='sm'
							color='gray.500'
							textAlign='center'
							mt={6}
							mb={2}
							fontWeight='600'
							textTransform='uppercase'
							letterSpacing='0.08em'>
							Or choose a different plan
						</Text>
					</Box>
				) : null}

				<PlanUpgradePicker
					plans={allPlansForPicker}
					isYearly={isYearly}
					onBillingCycleChange={setIsYearly}
					onSelectSubscriptionPlan={handlePlanSignUp}
					onSelectLifetimePlan={handleLifetimePlanSelect}
					isLoading={loadingPlans}
					stickyHeaderOffset={defaultSizes.headerHeight}
					firstTimeSubscribe
					showMoneyBackGuarantee={showMoneyBackGuarantee}
					tabsMb={6}
				/>
			</Box>

			<SubscriptionModal
				isOpen={isOpen}
				onClose={onClose}
				plan={selectedPlan}
				isYearly={isYearly}
				onSubscriptionSuccess={handleSubscriptionSuccess}
				currentSubscription={subscription}
				deferSubscriptionRefresh
			/>

			<LifetimeCheckoutModal
				isOpen={isLifetimeOpen}
				onClose={onLifetimeClose}
				plan={selectedLifetimePlan}
				onPurchaseSuccess={handleLifetimePurchaseSuccess}
				deferSubscriptionRefresh
			/>

			<Modal
				isOpen={isSuccessOpen}
				onClose={onSuccessClose}
				size='md'
				isCentered
				closeOnOverlayClick={false}
				closeOnEsc={false}>
				<ModalOverlay bg='blackAlpha.600' backdropFilter='blur(4px)' />
				<ModalContent mx={4} borderRadius='2xl' overflow='hidden'>
					<Box
						bgGradient='linear(135deg, teal.400 0%, cyan.500 100%)'
						px={6}
						py={8}
						textAlign='center'
						color='white'>
						<Flex
							align='center'
							justify='center'
							w={16}
							h={16}
							borderRadius='full'
							bg='whiteAlpha.300'
							mx='auto'
							mb={4}>
							<Icon as={FaCheckCircle} boxSize={8} />
						</Flex>
						<Heading size='lg' fontWeight='800' mb={2}>
							Welcome aboard!
						</Heading>
						<Text fontSize='sm' color='whiteAlpha.900' m={0}>
							Your plan is active
						</Text>
					</Box>
					<ModalBody px={6} py={6}>
						<VStack spacing={5} align='stretch'>
							<Text fontSize='md' textAlign='center' color='gray.600' m={0}>
								You're now on{' '}
								<Text as='span' fontWeight='700' color='teal.600'>
									{activePlanName}
								</Text>
								. Everything is ready — let's get your workspace set up.
							</Text>

							<Button
								colorScheme='teal'
								size='lg'
								width='full'
								leftIcon={<FaRocket />}
								onClick={handleGetToWork}
								isLoading={loadingBrandingCheck || isNavigating}
								borderRadius='lg'
								boxShadow='md'
								_hover={{ transform: 'translateY(-1px)', boxShadow: 'lg' }}>
								Let's get to work
							</Button>

							<Text fontSize='sm' color='gray.400' textAlign='center' m={0}>
								{hasBrandingAccess
									? 'Next: set up your workspace branding'
									: "You'll be taken to your dashboard"}
							</Text>
						</VStack>
					</ModalBody>
				</ModalContent>
			</Modal>
		</Box>
	);
};

export default SubscribeToPlanFirstTimePage;
